import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Shield,
  Download,
  Smartphone,
  FileText,
  QrCode,
  Import,
  CheckCircle,
  ArrowLeft,
} from "lucide-react";

export function InstructionsPage() {
  const steps = [
    {
      icon: Download,
      title: "Скачайте приложение",
      description:
        "Установите AmneziaWG на свое устройство. Приложение доступно для Android, iOS, Windows, macOS и Linux.",
      links: [
        { label: "Android (Google Play)", url: "https://play.google.com/store/apps/details?id=org.amnezia.awg&hl=ru" },
        { label: "iOS (App Store)", url: "https://apps.apple.com/ru/app/amneziawg/id6456296465" },
        { label: "GitHub (Android)", url: "https://github.com/amnezia-vpn/amneziawg-android" },
        { label: "GitHub (Windows)", url: "https://github.com/amnezia-vpn/amneziawg-windows" },
      ],
    },
    {
      icon: FileText,
      title: "Создайте конфигурацию",
      description:
        "Перейдите в личный кабинет (Мои конфиги) и нажмите «Создать конфиг». Скачайте .conf файл или отсканируйте QR-код камерой телефона.",
    },
    {
      icon: Import,
      title: "Импортируйте конфигурацию",
      description:
        "Откройте приложение AmneziaWG, нажмите «Импорт из файла» или «Импорт из QR-кода» и выберите полученную конфигурацию доступа.",
    },
    {
      icon: CheckCircle,
      title: "Подключитесь и проверьте",
      description:
        "Включите переключатель подключения в приложении. Убедитесь, что соединение установлено — индикатор станет зеленым. Готово!",
    },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur sticky top-0 z-10">
        <div className="flex h-16 items-center justify-between px-6 max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <Shield className="h-6 w-6 text-emerald-400" />
            <h1 className="text-xl font-bold">Secure Access Gateway</h1>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Назад в кабинет
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="p-6 max-w-4xl mx-auto space-y-8">
        <div className="text-center space-y-2">
          <h2 className="text-3xl font-bold">Инструкция по подключению</h2>
          <p className="text-slate-400 max-w-xl mx-auto">
            Следуйте шагам ниже, чтобы настроить защищенное соединение на вашем устройстве.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {steps.map((step, idx) => (
            <Card key={idx} className="border-slate-800 bg-slate-900 hover:border-slate-700 transition-colors">
              <CardHeader className="flex flex-row items-center gap-3 pb-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500/10">
                  <step.icon className="h-5 w-5 text-emerald-400" />
                </div>
                <div>
                  <div className="text-xs text-slate-500 font-medium">Шаг {idx + 1}</div>
                  <CardTitle className="text-base">{step.title}</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-slate-400 leading-relaxed">{step.description}</p>
                {step.links && (
                  <div className="flex flex-wrap gap-2">
                    {step.links.map((link) => (
                      <a
                        key={link.url}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs px-3 py-1.5 rounded-md bg-slate-800 text-emerald-400 hover:bg-slate-700 transition-colors"
                      >
                        {link.label}
                      </a>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="border-slate-800 bg-slate-900">
          <CardContent className="p-6 flex flex-col md:flex-row items-center gap-4 text-center md:text-left">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/10 shrink-0">
              <Smartphone className="h-6 w-6 text-amber-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-200">Нужна помощь?</h3>
              <p className="text-sm text-slate-400 mt-1">
                Если у вас возникли сложности при подключении, обратитесь к администратору системы.
              </p>
            </div>
            <Link to="/dashboard">
              <Button variant="outline" className="border-slate-700">
                <QrCode className="mr-2 h-4 w-4" />
                Перейти в кабинет
              </Button>
            </Link>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
